'use client';
import {Mobile} from '@components/Mobile';
import {ImgProps} from '@custom-types/ImgProps';
import {ButtonBase, SxProps} from '@mui/material';
import MuiBox from '@new-components/atoms/MuiBox';
import MuiContainer from '@new-components/atoms/MuiContainer';
import MuiDivider from '@new-components/atoms/MuiDivider';
import MuiGrid from '@new-components/atoms/MuiGrid';
import MuiTypography from '@new-components/atoms/MuiTypography';
import Image from 'next/image';
import {FC, useState} from 'react';

interface AmenitiesTitleProps {
  label: string;
}

const AmenitiesTitle: FC<AmenitiesTitleProps> = (props) => {
  return (
    <MuiTypography
      sx={{
        fontWeight: 500,
        fontSize: '22px',
        lineHeight: '125%',
        letterSpacing: '-0.4px',
        marginBottom: '32px',
      }}>
      {props.label}
    </MuiTypography>
  );
};

interface AmenitiesOptionProps {
  label: string;
  icon: ImgProps;
}

const AmenitiesOption: FC<AmenitiesOptionProps> = (props) => {
  return (
    <MuiBox sx={{display: 'flex', alignItems: 'center', columnGap: '16px'}}>
      <Image
        loading='eager'
        src={props.icon.src}
        alt={props.icon.alt}
        height={32}
        width={32}
        style={{objectFit: 'contain', width: '32px', height: '32px'}}
      />
      <MuiTypography
        sx={{
          fontWeight: 300,
          fontSize: '19px',
          lineHeight: '125%',
          letterSpacing: '-0.4px',
        }}>
        {props.label}
      </MuiTypography>
    </MuiBox>
  );
};

interface AmenitiesDividerProps {
  sx?: SxProps;
}
const AmenitiesDivider = (props: AmenitiesDividerProps) => {
  return (
    <MuiDivider
      variant='fullWidth'
      sx={{background: 'rgba(130, 128, 126, 0.1)', my: '24px', ...props.sx}}
    />
  );
};

export type AmenitiesOption =
  | (AmenitiesTitleProps & {type: 'title'})
  | (AmenitiesOptionProps & {type: 'option'});
interface AmenitiesBlockProps {
  options: Array<AmenitiesOption>;
  sx?: SxProps;
}

const AmenitiesBlock: FC<AmenitiesBlockProps> = (props) => {
  return (
    <MuiBox
      sx={{
        display: 'flex',
        flexDirection: 'column',
        maxWidth: {xs: '100%', md: '380px'},
        height: '100%',
        justifyContent: 'center',
        ...props.sx,
      }}>
      {props.options.map((option, index) => {
        return (
          <>
            {index && props.options[index - 1].type === 'option' ? (
              <AmenitiesDivider />
            ) : null}
            {option.type === 'title' ? (
              <AmenitiesTitle key={index} label={option.label} />
            ) : (
              <AmenitiesOption
                key={index}
                label={option.label}
                icon={option.icon}
              />
            )}
          </>
        );
      })}
    </MuiBox>
  );
};

interface AllAmenitiesBlockProps extends AmenitiesBlockProps {}

const AllAmenitiesBlock: FC<AllAmenitiesBlockProps> = (props) => {
  const [open, setOpen] = useState(false);
  const totalCountEven = !(props.options.length % 2);

  const handleToggle = () => {
    setOpen((prev) => !prev);
  };
  if (!props.options || props.options.length === 0) return null;

  return (
    <MuiContainer
      sx={{
        display: 'flex',
        flexDirection: 'column',
        rowGap: '32px',
        marginTop: {md: '-50px'},
        pb: {xs: '24px', md: '96px'},
      }}>
      {open && (
        <MuiGrid container columnSpacing={8}>
          {props.options.map((option, index) => {
            const isDivided = totalCountEven
              ? index < props.options.length - 2
              : index < props.options.length - 1;
            const isLast = index === props.options.length - 1;
            const isFirst = index === 0;
            return (
              <MuiGrid item xs={12} md={6} key={index}>
                {isFirst && (
                  <Mobile>
                    {isDivided ? (
                      <AmenitiesDivider sx={{mt: '0'}} />
                    ) : null}
                  </Mobile>
                )}
                {totalCountEven && isLast ? (
                  <Mobile>
                    <AmenitiesDivider />
                  </Mobile>
                ) : null}
                {option.type === 'title' ? (
                  <AmenitiesTitle key={index} label={option.label} />
                ) : (
                  <AmenitiesOption
                    key={index}
                    label={option.label}
                    icon={option.icon}
                  />
                )}
                {isDivided ? <AmenitiesDivider /> : null}
              </MuiGrid>
            );
          })}
        </MuiGrid>
      )}
      <ButtonBase
        onClick={handleToggle}
        sx={{
          height: '40px',
          padding: '10px 20px',
          borderRadius: '500px',
          border: '1px solid rgba(186, 186, 200, 1)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          width: 'fit-content',
          marginTop: '20px',
        }}>
        <MuiTypography
          sx={{
            fontWeight: 400,
            fontSize: '15px',
            lineHeight: '18.75px',
            letterSpacing: '-0.4px',
            textAlign: 'center',
            color: 'rgba(45, 45, 45, 1)',
          }}>
          {open ? 'Show less amenities' : `Show all amenities`}
        </MuiTypography>
      </ButtonBase>
    </MuiContainer>
  );
};

export interface CommunityAmenitiesProps {
  headerLabel: string;
  primaryAmenities: AmenitiesBlockProps['options'];
  secondaryAmenities: AmenitiesBlockProps['options'];
  otherAmenities?: AllAmenitiesBlockProps['options'];
  primaryImg: ImgProps;
  secondaryImg: ImgProps;
  secondaryShadowImg: ImgProps;
}

const CommunityAmenitiesBlock: FC<CommunityAmenitiesProps> = (props) => {
  if (!props.primaryAmenities || !props.primaryAmenities.length) {
    return null;
  }

  return (
    <MuiBox sx={{display: 'flex', flexDirection: 'column'}}>
      <MuiContainer
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          pt: {md: '128px'},
          paddingBottom: {xs: '32px', md: '96px'},
        }}>
        <MuiTypography
          sx={{
            fontWeight: {xs: 400, md: 300},
            fontSize: {xs: '24px', md: '48px'},
            lineHeight: {xs: '30px', md: '60px'},
            letterSpacing: {xs: '-0.4px', md: '-0.8px'},
            color: '#003243',
            textAlign: 'center',
            maxWidth: {xs: 'auto', md: '620px'},
          }}>
          {props.headerLabel}
        </MuiTypography>
      </MuiContainer>
      <MuiContainer
        sx={{
          display: 'flex',
          gap: {xs: '32px', md: '96px'},
          flexDirection: {xs: 'column', md: 'row'},
          paddingX: {md: '0px', lg: '0px'},
          pb: {xs: '24px', md: '96px'},
        }}>
        <MuiBox
          sx={{
            height: {xs: '300px', md: '603px'},
            width: {sx: '100%', md: '60%'},
            maxWidth: '803px',
            boxShadow: ' 0px 2px 10px 0px rgba(0, 0, 0, 0.04)',
            borderTopRightRadius: '24px',
            borderBottomRightRadius: '24px',
            borderTopLeftRadius: {xs: '24px', md: '0'},
            borderBottomLeftRadius: {xs: '24px', md: '0'},
            overflow: 'hidden',
          }}>
          <Image
            src={props.primaryImg.src}
            alt={props.primaryImg.alt}
            width={803}
            height={603}
            style={{width: '100%', height: '100%', objectFit: 'cover'}}
          />
        </MuiBox>
        <AmenitiesBlock options={props.primaryAmenities} />
      </MuiContainer>
      {props.secondaryAmenities && props.secondaryAmenities.length > 0 && (
        <MuiContainer
          sx={{
            display: 'flex',
            minHeight: '563px',
            flexDirection: {xs: 'column-reverse', md: 'row'},
            gap: {xs: '32px', md: '0'},
            pb: {xs: '24px', md: '96px'},
          }}>
          <MuiBox sx={{flex: 1}}>
            <AmenitiesBlock options={props.secondaryAmenities} />
          </MuiBox>
          <MuiBox
            sx={{
              position: 'relative',
              minWidth: {xs: '100%', md: '750px'},
              minHeight: '300px',
              flex: 1,
              pr: '12px',
              pb: {xs: '24px', md: '96px'},
            }}>
            <MuiBox
              sx={{
                boxShadow: ' 0px 2px 10px 0px rgba(0, 0, 0, 0.04)',
                borderRadius: '24px',
                overflow: 'hidden',
                width: {xs: '100%', md: '656px'},
                height: {xs: '300px', md: '517px'},
                zIndex: 1,
                position: 'relative',
              }}>
              <Image
                src={props.secondaryImg.src}
                alt={props.secondaryImg.alt}
                width={656}
                height={517}
                style={{objectFit: 'cover', width: '100%', height: '100%'}}
              />
            </MuiBox>
            <MuiBox
              sx={{
                display: {xs: 'none', md: 'block'},
                position: 'absolute',
                top: {xs: '12px', md: '43px'},
                left: {xs: '12px', md: '84px'},
                boxShadow: '0px 2px 10px 0px rgba(0, 0, 0, 0.04)',
                borderRadius: '24px',
                overflow: 'hidden',
                width: {xs: '100%', md: '656px'},
                height: {xs: '300px', md: '517px'},
              }}>
              <Image
                src={props.secondaryShadowImg.src}
                alt={props.secondaryShadowImg.alt}
                width={656}
                height={517}
                style={{objectFit: 'cover', width: '100%', height: '100%'}}
              />
            </MuiBox>
          </MuiBox>
        </MuiContainer>
      )}
      {props.otherAmenities && (
        <AllAmenitiesBlock options={props.otherAmenities} />
      )}
    </MuiBox>
  );
};

export default CommunityAmenitiesBlock;
